/**
 * The public homepage: the first page this product has had that does not ask you to sign in.
 *
 * Until now `/` sat inside the shell, so every visitor to the domain met a sign-in wall. The two
 * calls to action are deliberately in this order: the example asks for nothing, and showing the
 * product before requesting an account is what earns the account.
 *
 * It runs over the same looping video the sign-in page uses, for two reasons - it is already the
 * product's own backdrop, and a homepage that looks like the thing you are about to use is a
 * promise kept. The poster frame carries the first paint and the video arrives behind it, because
 * this is now the page most people will load first and it should not wait on three megabytes.
 */

import { ArrowRight, PlayCircle } from "lucide-react";
import { Link } from "react-router";

import { buttonVariants } from "@/components/ui/button";
import { useBranding } from "@/lib/useBranding";

const STEPS = [
  { n: 1, title: "Audience", body: "The general public, or a community built for your scenario and sampled from census data." },
  { n: 2, title: "Message", body: "Paste it exactly as people will see it." },
  { n: 3, title: "Promises", body: "The facts every version must keep. Suggested edits can never quietly drop them." },
  { n: 4, title: "Ask", body: "They read it, post, reply and follow each other over several rounds. You watch opinion move." },
];

const TRUST = [
  {
    title: "Sampled, not imagined",
    body: "Age, education and occupation come from ONS Census 2021 for UK audiences. Every audience shows its measured fit against the real distribution — a number, not an adjective.",
  },
  {
    title: "Every figure carries its uncertainty",
    body: "Nothing is reported as more certain than it is. If the difference between two drafts is within the noise, you are told so rather than handed a winner.",
  },
  {
    title: "Personas are screened before they are used",
    body: "Each answers two matched questionnaires. Any whose answers contradict themselves are excluded, and you can read the contradiction in plain English.",
  },
  {
    title: "It says when editing cannot fix it",
    body: "Goal seek works out the ceiling — the best you could do if every concern were addressed. If your target sits above it, the answer is to change the offer, not the wording.",
  },
];

export function HomePage() {
  const { brand } = useBranding();

  return (
    <div className="relative min-h-dvh overflow-hidden bg-black">
      <video
        className="pointer-events-none fixed inset-0 h-full w-full object-cover opacity-45"
        src={brand.login_video_url}
        poster={brand.login_poster_url}
        autoPlay
        loop
        muted
        playsInline
        preload="none"
        aria-hidden="true"
      />
      <div aria-hidden="true" className="pointer-events-none fixed inset-0 bg-gradient-to-b from-black/50 via-black/70 to-black" />

      <header className="relative z-10 mx-auto flex max-w-5xl items-center justify-between gap-4 px-6 py-4">
        <img src={brand.logo_url} alt={brand.product_name} className="block h-9 w-auto" />
        <nav className="flex items-center gap-1 text-sm">
          <Link to="/examples" className="px-3 py-1.5 text-muted-foreground hover:text-foreground">
            Examples
          </Link>
          <Link to="/pricing" className="px-3 py-1.5 text-muted-foreground hover:text-foreground">
            Pricing
          </Link>
          <Link to="/login" className={buttonVariants({ size: "sm" })}>
            Sign in
          </Link>
        </nav>
      </header>

      <main className="relative z-10 mx-auto max-w-5xl px-6 pb-20">
        <section className="py-16 md:py-24">
          <h1 className="max-w-3xl text-4xl leading-[1.1] font-semibold tracking-tight text-white md:text-6xl">
            Test the message before the world does.
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-slate-300 md:text-xl">
            Run your copy past a census-grounded community of AI personas. See who lights up, who
            pushes back, and which sentences drove it — with intervals, not gut feel.
          </p>
          <div className="mt-9 flex flex-wrap gap-3">
            <Link to="/examples" className={buttonVariants({ size: "lg" })}>
              <PlayCircle aria-hidden="true" className="size-4" /> See an example message test
            </Link>
            <Link to="/login" className={buttonVariants({ variant: "outline", size: "lg" })}>
              Run a free message test <ArrowRight aria-hidden="true" className="size-4" />
            </Link>
          </div>
          <p className="mt-4 text-sm text-slate-400">
            The example needs no account. Your first runs are on us.
          </p>
        </section>

        <section className="border-t border-white/10 py-14">
          <h2 className="text-2xl font-semibold">Every announcement has one sentence that does the damage</h2>
          <p className="mt-3 max-w-2xl text-slate-300">
            It is rarely the one anyone argued about in the draft, and you usually find out which it
            was from the replies. Research would tell you first — in three weeks, for more than most
            messages are worth. So most messages go out untested, and the ones that go wrong go
            wrong in public.
          </p>
        </section>

        <section className="border-t border-white/10 py-14">
          <h2 className="text-2xl font-semibold">Four steps</h2>
          <ol className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((step) => (
              <li key={step.n} className="rounded-xl border border-white/12 bg-black/55 p-4 backdrop-blur-sm">
                <span className="mb-2 inline-flex size-7 items-center justify-center rounded-full bg-white/10 text-sm font-bold">
                  {step.n}
                </span>
                <h3 className="font-semibold text-white">{step.title}</h3>
                <p className="mt-1 text-sm text-slate-300">{step.body}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className="border-t border-white/10 py-14">
          <h2 className="text-2xl font-semibold">Why you can trust the numbers</h2>
          <div className="mt-6 grid gap-4 md:grid-cols-2">
            {TRUST.map((item) => (
              <div key={item.title} className="rounded-xl border border-white/12 bg-black/55 p-5 backdrop-blur-sm">
                <h3 className="font-semibold text-white">{item.title}</h3>
                <p className="mt-1 text-sm text-slate-300">{item.body}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="border-t border-white/10 py-14">
          <h2 className="text-2xl font-semibold">Said plainly</h2>
          <p className="mt-3 max-w-2xl text-slate-300">
            {brand.product_name} is a rehearsal, not a survey. These are language models playing a
            part, not people, and it is not a validated research instrument. It will not tell you
            what the public thinks.
          </p>
          <p className="mt-3 max-w-2xl text-slate-300">
            What it will do is find the line that causes trouble, sort six drafts down to two, and
            tell you which questions are worth paying real research to answer — in the time it takes
            to make a coffee.
          </p>
        </section>

        <section className="border-t border-white/10 py-14 text-center">
          <h2 className="text-2xl font-semibold">Start with an example. It asks for nothing.</h2>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/examples" className={buttonVariants({ size: "lg" })}>
              See an example message test
            </Link>
            <Link to="/login" className={buttonVariants({ variant: "outline", size: "lg" })}>
              Run a free message test
            </Link>
          </div>
        </section>

        <footer className="flex flex-wrap items-center justify-center gap-4 border-t border-white/10 pt-8 text-xs text-slate-400">
          <Link to="/pricing" className="hover:text-foreground">Pricing</Link>
          <Link to="/examples" className="hover:text-foreground">Examples</Link>
          <Link to="/terms" className="hover:text-foreground">Terms</Link>
          <Link to="/privacy" className="hover:text-foreground">Privacy</Link>
        </footer>
      </main>
    </div>
  );
}
